import axios from 'axios';
import Vue from 'vue';

/**
 * Cache successful image payloads as data URLs so repeat renders
 * do not hit the network for the same resource.
 * @type {Map<string, string>}
 */
const resolvedImageSrcByUrl = new Map();

/**
 * Track image URLs that already failed (typically 404) so we can
 * short-circuit directly to fallback on subsequent renders.
 * @type {Set<string>}
 */
const failedImageUrls = new Set();

/**
 * De-duplicate concurrent requests for the same URL.
 * Multiple components can await the same Promise instead of
 * triggering parallel identical HTTP calls.
 * @type {Map<string, Promise<string|null>>}
 */
const inFlightImageRequests = new Map();

/**
 * v-auth-src (your existing code - unchanged except the if check we discussed)
 */
const setImgSrc = (el, binding) => {
   if (binding.oldValue === undefined || binding.value !== binding.oldValue) {
      const imageUrl = binding?.value?.src;
      const fallbackUrl = binding?.value?.fallbackUrl;

      if (!imageUrl) {
         if (fallbackUrl) {
            el.src = fallbackUrl;
         }
         return;
      }

      if (resolvedImageSrcByUrl.has(imageUrl)) {
         el.src = resolvedImageSrcByUrl.get(imageUrl);
         return;
      }

      if (failedImageUrls.has(imageUrl)) {
         if (fallbackUrl) {
            el.src = fallbackUrl;
         }
         return;
      }

      // Either reuse the existing request for this URL or start one.
      let request = inFlightImageRequests.get(imageUrl);
      if (!request) {
         request = axios({
            method: 'get',
            url: imageUrl,
            responseType: 'arraybuffer',
         })
            .then((response) => {
               const mimeType = response.headers['content-type'].toLowerCase();
               const imgBase64 = Buffer.from(response.data, 'binary').toString('base64');
               const resolvedSrc = `data:${mimeType};base64,${imgBase64}`;
               resolvedImageSrcByUrl.set(imageUrl, resolvedSrc);
               return resolvedSrc;
            })
            .catch(() => {
               failedImageUrls.add(imageUrl);
               return null;
            })
            .finally(() => {
               inFlightImageRequests.delete(imageUrl);
            });
         inFlightImageRequests.set(imageUrl, request);
      }

      request.then((resolvedSrc) => {
         if (resolvedSrc) {
            el.src = resolvedSrc;
         } else if (fallbackUrl) {
            el.src = fallbackUrl;
         }
      });
   }
};

Vue.directive('auth-src', {
   bind: setImgSrc,
   componentUpdated: setImgSrc,
});

/**
 * v-grid-keynav
 * Adds grid keyboard navigation to focusable elements.
 * Expects binding.value = { section, tableIndex, rowIndex, colIndex, type? }
 * Calls vm.focusCell() with updated coords.
 */
const ARROW_KEYS = ['ArrowUp', 'ArrowDown', 'Enter', 'NumpadEnter'];

const handleGridKeydown = (el, binding, vnode) => {
   if (binding.oldValue === undefined || binding.value !== binding.oldValue) {
      el.__gridKeyNav__ = async (event) => {
         // Regular grid navigation (only non-shift keys)
         if (!ARROW_KEYS.includes(event.key)) return;

         event.preventDefault();

         const {section, tableIndex, rowIndex, colIndex, type} = binding.value;
         let nextRow = rowIndex;
         let nextCol = colIndex;

         if (event.key === 'Enter' && event.shiftKey) {
            const inputEl =
               el.tagName === 'INPUT'
                  ? el
                  : el.querySelector('input[type="text"], input[type="number"], input');

            if (inputEl && inputEl.placeholder && inputEl.value === '') {
               inputEl.value = inputEl.placeholder;
               inputEl.dispatchEvent(new Event('input', {bubbles: true}));

               const component = vnode.componentInstance;
               const saveMethod =
                  type === 'project'
                     ? 'setProjectPercentage'
                     : type === 'activity'
                     ? 'setActivityPercentage'
                     : type === 'percentage'
                     ? 'setPercentage'
                     : null;

               if (component && saveMethod && typeof component[saveMethod] === 'function') {
                  try {
                     await component[saveMethod](inputEl.value);
                  } catch {
                     // The component save path handles user-facing errors.
                  }
               }

               await vnode.context.$nextTick();
               vnode.context.focusCell({
                  section,
                  tableIndex,
                  rowIndex,
                  colIndex: colIndex + 1,
                  type,
                  colStep: 1,
               });
            }
            return;
         }

         if (event.key === 'ArrowUp') nextRow = rowIndex - 1;
         if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === 'NumpadEnter')
            nextRow = rowIndex + 1;
         vnode.context.focusCell({
            section,
            tableIndex,
            rowIndex: nextRow,
            colIndex: nextCol,
            type,
            rowStep: nextRow - rowIndex,
         });
      };
   }
};

Vue.directive('grid-keynav', {
   bind(el, binding, vnode) {
      handleGridKeydown(el, binding, vnode);
      el.addEventListener('keydown', el.__gridKeyNav__);
   },
   componentUpdated(el, binding, vnode) {
      handleGridKeydown(el, binding, vnode);
      el.removeEventListener('keydown', el.__gridKeyNav__);
      el.addEventListener('keydown', el.__gridKeyNav__);
   },
   unbind(el) {
      el.removeEventListener('keydown', el.__gridKeyNav__);
      delete el.__gridKeyNav__;
   },
});
